import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';
import { useUIStore } from '@/stores/ui-store';
import { IS_RUMIO } from '@/config/site';
import { resolveAppNavIcon } from '@/lib/app-nav-icon';
import { canAccessAdminShell } from '@/lib/roles';
import { MobileNavLink, navLinkKey } from './NavLinks';
import type { NavDropdowns } from './NavLinks';
import { adminLinkDest } from './nav-types';
import type { AppNavSection, NavLink } from './nav-types';

interface MobileMenuProps {
  navLinks: NavLink[];
  visibleAppSections: AppNavSection[];
  showEcommerce: boolean;
  showCourses: boolean;
  storeName: string;
  inReplaceMode: boolean;
  dropdowns: NavDropdowns;
  /**
   * `drawer` (the original): an 85%-wide panel sliding in from the right over
   * a dimmed backdrop. `sheet`: a full-screen panel on the page surface —
   * what app-launch headers use, with room for a pinned footer slot.
   */
  layout?: 'drawer' | 'sheet';
  /** Pinned to the bottom of the panel (a template's store badges). */
  footer?: ReactNode;
}

const ROW_CLASS =
  'block px-3 py-3 text-base font-medium text-sage-800 hover:bg-sage-50 rounded-lg min-h-[44px] flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600';

/**
 * The mobile menu every header variant shares. Rendered as a sibling of
 * <header> (not in-flow) so it overlays page content at z-50 with an opaque
 * panel, instead of expanding the sticky header. Owns the Escape key and the
 * background scroll lock while open; returns null while closed.
 */
export default function MobileMenu({
  navLinks,
  visibleAppSections,
  showEcommerce,
  showCourses,
  storeName,
  inReplaceMode,
  dropdowns,
  layout = 'drawer',
  footer,
}: MobileMenuProps) {
  const { t } = useTranslation(['nav', 'common']);
  const { user, logout } = useAuthStore();
  const { isMobileMenuOpen, closeMobileMenu } = useUIStore();
  const navigate = useNavigate();

  // Close on Escape + lock background scroll while the overlay is open.
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        closeMobileMenu();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen, closeMobileMenu]);

  if (!isMobileMenuOpen) return null;

  const isSheet = layout === 'sheet';

  return (
    <div className="lg:hidden">
      <div
        className="fixed inset-0 z-50 bg-black/50 animate-in fade-in duration-200"
        onClick={closeMobileMenu}
        aria-hidden="true"
      />
      <nav
        id="mobile-menu"
        className={cn(
          'fixed z-50 flex flex-col shadow-xl animate-in duration-200',
          isSheet
            ? 'inset-0 bg-surface slide-in-from-top'
            : 'inset-y-0 right-0 w-[85%] max-w-sm bg-cream slide-in-from-right',
        )}
        aria-label={t('nav:mobileNavigation')}
      >
        <div className={cn('flex h-16 flex-none items-center justify-between px-4', isSheet ? 'border-b border-line' : 'border-b border-sage-200')}>
          <span className="truncate font-display text-lg font-bold text-sage-900">{storeName}</span>
          <button
            type="button"
            onClick={closeMobileMenu}
            className="-mr-2 flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-2 text-sage-800 hover:text-sage-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
            aria-label={t('nav:closeMenu')}
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
          {navLinks.map((link) => (
            <MobileNavLink
              key={navLinkKey(link)}
              link={link}
              dropdowns={dropdowns}
              closeMobileMenu={closeMobileMenu}
              inReplaceMode={inReplaceMode}
            />
          ))}
          {!user && (
            <Link
              to="/login"
              className="block px-3 py-3 text-base font-medium text-sage-600 hover:bg-sage-50 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
              onClick={closeMobileMenu}
            >
              {t('nav:signInOrCreate')}
            </Link>
          )}
          {/* User-account links inside the mobile drawer. The avatar dropdown
              in the header is the desktop surface, but on mobile the drawer
              is the more discoverable path — especially when
              `hide_nav_when_authed` is true and the marketing nav is empty,
              which would otherwise leave authed users with a blank panel. */}
          {user && (
            <div className="pt-2 mt-2 border-t border-sage-200 space-y-1">
              <div className="px-3 py-1 text-xs uppercase tracking-wide text-sage-500">
                {user.firstName || user.email}
              </div>
              <Link to="/account" className={ROW_CLASS} onClick={closeMobileMenu}>
                {t('nav:myAccount')}
              </Link>
              {showEcommerce && (
                <Link to="/account/orders" className={ROW_CLASS} onClick={closeMobileMenu}>
                  {t('nav:orders')}
                </Link>
              )}
              {showCourses && (
                <Link to="/account/courses" className={ROW_CLASS} onClick={closeMobileMenu}>
                  {t('nav:myCourses')}
                </Link>
              )}
              {canAccessAdminShell(user.role) && (
                <Link to={adminLinkDest(user.role)} className={cn(ROW_CLASS, 'text-sage-700')} onClick={closeMobileMenu}>
                  {t('nav:adminPanel')}
                </Link>
              )}
              {IS_RUMIO && (
                <Link to="/onboarding" className={cn(ROW_CLASS, 'text-sage-700')} onClick={closeMobileMenu}>
                  {t('nav:createNewSite')}
                </Link>
              )}
              {visibleAppSections.map((section) => (
                <div key={section.appId} className="pt-1">
                  <div className="border-t border-sage-200 my-1" aria-hidden />
                  <div className="px-3 py-1 text-xs uppercase tracking-wide text-sage-500">
                    {section.appName}
                  </div>
                  {section.items.map((item) => {
                    if (!item.href) return null;
                    const Icon = resolveAppNavIcon(item.icon);
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        className="flex items-center gap-2 px-3 py-3 text-base font-medium text-sage-800 hover:bg-sage-50 rounded-lg min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
                        onClick={closeMobileMenu}
                      >
                        {Icon && <Icon className="h-4 w-4" aria-hidden />}
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              ))}
              <button
                type="button"
                className="block w-full text-left px-3 py-3 text-base font-medium text-red-600 hover:bg-red-50 rounded-lg min-h-[44px] flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
                onClick={() => { closeMobileMenu(); logout(); navigate('/'); }}
              >
                {t('nav:signOut')}
              </button>
            </div>
          )}
        </div>
        {footer && (
          <div className={cn('flex-none px-4 py-4', isSheet ? 'border-t border-line' : 'border-t border-sage-200')}>
            {footer}
          </div>
        )}
      </nav>
    </div>
  );
}
